import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Modal } from "../primitives/Modal";
import { useChainStore, cfBus } from "../../data/chainStore";
import { PremiumInput } from "../brand/PremiumInput";
import { PremiumSelect } from "../brand/PremiumSelect";
import { PremiumTextarea } from "../brand/PremiumTextarea";

const initialForm = (preset = {}) => ({
  name: preset.name || "",
  stage: preset.stage || "offer",
  status: preset.status || "Active",
  risk: preset.risk || "Low",
  fee: preset.fee ?? "",
  agreedPrice: preset.agreedPrice || "",
  targetExchange: preset.targetExchange || "",
  targetCompletion: preset.targetCompletion || "",
  buyerId: preset.buyerId || "",
  sellerId: preset.sellerId || "",
  agentId: preset.agentId || "",
  solicitorId: preset.solicitorId || "",
  buyerSolicitorId: preset.buyerSolicitorId || "",
  brokerId: preset.brokerId || "",
  notes: preset.notes || "",
});

export const EditDealModal = () => {
  const { updateDeal, deleteDeal, contacts, stages, dealStatuses, riskLevels } = useChainStore();
  const [open, setOpen] = useState(false);
  const [deal, setDeal] = useState(null);
  const [form, setForm] = useState(initialForm());

  useEffect(() => {
    return cfBus.on("cf:open-edit-deal", (d) => {
      setDeal(d);
      setForm(initialForm(d || {}));
      setOpen(true);
    });
  }, []);

  const close = () => { setOpen(false); setDeal(null); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!deal) return;
    const patch = {
      ...form,
      fee: form.fee === "" ? null : String(form.fee),
    };
    try {
      await updateDeal(deal.id, patch);
      toast.success("Deal updated");
      close();
    } catch (err) {
      toast.error("Could not save changes", { description: err?.response?.data?.detail || err?.message || "Network error" });
    }
  };

  const onDelete = async () => {
    if (!deal) return;
    if (!window.confirm(`Delete this deal? This cannot be undone.`)) return;
    try {
      await deleteDeal(deal.id);
      toast.success("Deal deleted");
      close();
    } catch (err) {
      toast.error("Could not delete deal", { description: err?.response?.data?.detail || err?.message || "Network error" });
    }
  };

  const filterByRole = (role) => contacts.filter((c) => c.role === role);
  const buyers     = filterByRole("Buyer");
  const sellers    = filterByRole("Seller");
  const agents     = filterByRole("Estate Agent");
  const solicitors = filterByRole("Solicitor");
  const brokers    = filterByRole("Mortgage Broker");

  return (
    <Modal
      open={open}
      onClose={close}
      title="Edit deal"
      eyebrow="Deals"
      testid="edit-deal-modal"
      width={680}
      footer={
        <>
          <button onClick={onDelete} className="btn btn-ghost text-rose-600 hover:text-rose-700" data-testid="edit-deal-delete">Delete</button>
          <span className="ml-auto" />
          <button onClick={close} className="btn btn-ghost" data-testid="edit-deal-cancel">Cancel</button>
          <button onClick={submit} className="btn btn-primary" data-testid="edit-deal-save">Save changes</button>
        </>
      }
    >
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <PremiumInput label="Deal name (optional)" value={form.name} onChange={(v) => set("name", v)} placeholder="e.g. Wellington Crescent — Whitfield" testid="edit-deal-name" />
        </div>

        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Stage" value={form.stage} onChange={(v) => set("stage", v)} testid="edit-deal-stage"
            options={stages.map((s) => ({ value: s.id, label: s.label }))} />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Status" value={form.status} onChange={(v) => set("status", v)} testid="edit-deal-status"
            options={dealStatuses.map((s) => ({ value: s, label: s }))} />
        </div>

        <div className="col-span-12 md:col-span-4">
          <PremiumSelect label="Risk level" value={form.risk} onChange={(v) => set("risk", v)} testid="edit-deal-risk"
            options={riskLevels.map((r) => ({ value: r, label: r }))} />
        </div>
        <div className="col-span-12 md:col-span-4">
          <PremiumInput label="Agreed price" format="currency" value={form.agreedPrice} onChange={(v) => set("agreedPrice", v)} placeholder="745,000" testid="edit-deal-price" />
        </div>
        <div className="col-span-12 md:col-span-4">
          <PremiumInput label="Fee" format="currency" value={String(form.fee ?? "")} onChange={(v) => set("fee", v)} placeholder="14900" testid="edit-deal-fee" />
        </div>

        <div className="col-span-12 md:col-span-6">
          <PremiumInput label="Target exchange" type="date" value={form.targetExchange} onChange={(v) => set("targetExchange", v)} testid="edit-deal-target-exchange" />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumInput label="Target completion" type="date" value={form.targetCompletion} onChange={(v) => set("targetCompletion", v)} testid="edit-deal-target-completion" />
        </div>

        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Buyer" value={form.buyerId} onChange={(v) => set("buyerId", v)} placeholder="Unassigned" clearable searchable={buyers.length > 6} testid="edit-deal-buyer"
            options={buyers.map((c) => ({ value: c.id, label: c.name, hint: c.company }))} />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Seller" value={form.sellerId} onChange={(v) => set("sellerId", v)} placeholder="Unassigned" clearable searchable={sellers.length > 6} testid="edit-deal-seller"
            options={sellers.map((c) => ({ value: c.id, label: c.name, hint: c.company }))} />
        </div>

        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Lead negotiator (agent)" value={form.agentId} onChange={(v) => set("agentId", v)} placeholder="Unassigned" clearable searchable={agents.length > 6} testid="edit-deal-agent"
            options={agents.map((c) => ({ value: c.id, label: c.name, hint: c.company }))} />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Mortgage broker" value={form.brokerId} onChange={(v) => set("brokerId", v)} placeholder="No broker (cash buyer)" clearable searchable={brokers.length > 6} testid="edit-deal-broker"
            options={brokers.map((c) => ({ value: c.id, label: c.name, hint: c.company }))} />
        </div>

        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Seller solicitor" value={form.solicitorId} onChange={(v) => set("solicitorId", v)} placeholder="Unassigned" clearable searchable={solicitors.length > 6} testid="edit-deal-seller-solicitor"
            options={solicitors.map((c) => ({ value: c.id, label: c.name, hint: c.company }))} />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Buyer solicitor" value={form.buyerSolicitorId} onChange={(v) => set("buyerSolicitorId", v)} placeholder="Unassigned" clearable searchable={solicitors.length > 6} testid="edit-deal-buyer-solicitor"
            options={solicitors.map((c) => ({ value: c.id, label: c.name, hint: c.company }))} />
        </div>

        <div className="col-span-12">
          <PremiumTextarea label="Notes" value={form.notes} onChange={(v) => set("notes", v)} placeholder="Internal notes about this deal…" rows={3} autoResize maxRows={6} testid="edit-deal-notes" />
        </div>
      </div>
    </Modal>
  );
};
